import React from 'react'
import { IEditUser, IUser } from '../../../../api/types/user'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutationEditUser } from '../../../../api/mutations'
import { ErrorExplanation } from '../../../../components/ErrorExplanation/ErrorExplanation'
import { SubmitButton } from '../../../../components/SubmitButton/SubmitButton'
import { EditIconButton } from '../../../../components/EditIconButton/EditIconButton'
import { DialogActions, DialogContent, Modal } from '../../../../components/Modal/Modal'
import { FormRow } from '../../../../components/FormRow/FormRow'
import { Checkbox, FormControlLabel, TextField } from '@mui/material'
import { useShow } from '../../../../hooks/useShow'
import { useApiErrors } from '../../../../hooks/useApiErrors'
import { extractMessage } from '../../../../utils'
import { usersQueryKey } from '../../../../api/queries'

interface Props {
  user: IUser
}

export const EditUser: React.FC<Props> = ({ user }) => {
  const { show, doHide, doShow } = useShow()
  const { apiErrors, setApiErrors, setApiErrorsFromAxios } = useApiErrors()
  const { register, handleSubmit, errors, getValues } = useForm<IEditUser>()
  const queryClient = useQueryClient()

  const { mutate: editUserMutation, isLoading: isMutating } = useMutation(
    (data: IEditUser) => mutationEditUser(user.id, data),
    {
      onError: (err: any) => {
        setApiErrorsFromAxios(err, extractMessage(err) || 'Failed to update user')
      },
      onSuccess: async () => {
        setApiErrors([])
        queryClient.invalidateQueries(usersQueryKey()).then(doHide)
      },
    }
  )
  const onSubmit = (data: IEditUser) => editUserMutation(data)

  return (
    <>
      <EditIconButton onClick={doShow} />

      <Modal open={show} onClose={doHide} title="Edit user">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <FormRow>
              <ErrorExplanation errors={apiErrors} />
            </FormRow>
            <FormRow>
              <TextField
                name="username"
                label="Username"
                fullWidth={true}
                variant="outlined"
                defaultValue={user.username}
                inputProps={{
                  ref: register({
                    required: 'Required',
                  }),
                }}
                error={!!errors.username}
                helperText={errors.username && errors.username.message}
              />
            </FormRow>

            <FormRow>
              <TextField
                type="password"
                name="password"
                label="Password"
                fullWidth={true}
                variant="outlined"
                inputProps={{
                  ref: register({
                    minLength: {
                      value: 8,
                      message: 'Must have at least 8 characters',
                    },
                  }),
                }}
                error={!!errors.password}
                helperText={errors.password && errors.password.message}
              />
            </FormRow>

            <FormRow>
              <TextField
                type="password"
                name="passwordConfirmation"
                label="Password confirmation"
                fullWidth={true}
                variant="outlined"
                inputProps={{
                  ref: register({
                    validate: value =>
                      value === getValues('password') || 'The passwords do not match',
                  }),
                }}
                error={!!errors.passwordConfirmation}
                helperText={errors.passwordConfirmation && errors.passwordConfirmation.message}
              />
            </FormRow>

            <FormRow>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label="Admin"
                labelPlacement="end"
                name="admin"
                inputRef={register}
              />
            </FormRow>
            <FormRow>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label="GM"
                labelPlacement="end"
                name="gm"
                inputRef={register}
              />
            </FormRow>
          </DialogContent>
          <DialogActions>
            <SubmitButton disabled={isMutating}>Update</SubmitButton>
          </DialogActions>
        </form>
      </Modal>
    </>
  )
}
